import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowLeftOutlined, PartitionOutlined, TagOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Popconfirm, Radio, Space, Steps, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { ReleaseModalStepOne } from "./release-modal-step-one.js";
import { ReleaseModalStepTwo } from "./release-modal-step-two.js";
import { useContentBlockerTemplateMetaData } from "../../../../../hooks/content-blocker/useContentBlockerTemplateMetaData.js";
import { useServiceTemplateMetaData } from "../../../../../hooks/service/useServiceTemplateMetaData.js";
import { useDeviceType } from "../../../../../hooks/useDeviceType.js";
import { useReleaseCreate } from "../../../../../hooks/useReleaseCreate.js";
import { EnvironmentTag } from "../../../../../others/environment.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { DeviceType } from "../../../../../types/device/device.js";
import { QueryKeys } from "../../../../../util/query-keys.js";
import { validateTemplateSuccessorOfIdentifier } from "../../../../../util/release-validation.js";
const ReleaseModal = (props)=>{
    const { t } = useTranslation();
    const { notificationStore } = useStores();
    const device = useDeviceType();
    const queryClient = useQueryClient();
    const { open, title, onCancel, onReleased } = props;
    // Modal states
    const [currentStep, setCurrentStep] = useState(0);
    const [releaseRefId, setReleaseRefId] = useState();
    const [releasableIds, setReleasableIds] = useState({});
    const [serviceValidations, setServiceValidations] = useState(undefined);
    const [contentBlockerValidations, setContentBlockerValidations] = useState(undefined);
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedContentBlockers, setSelectedContentBlockers] = useState([]);
    // Deliberately starts undefined: the release must not run before an explicit yes/no decision was made
    const [disablePreReleaseClients, setDisablePreReleaseClients] = useState(undefined);
    const [isValid, setIsValid] = useState(false); // default is false
    const canRelease = isValid && disablePreReleaseClients !== undefined;
    const releaseDisabledReason = !isValid ? t("Resolve the reported template issues before releasing.") : disablePreReleaseClients === undefined ? t("Decide whether the pre-release clients should be deactivated after the release.") : undefined;
    // Modal queries
    const { data: serviceTemplateMetaData } = useServiceTemplateMetaData(currentStep === 1, false, false, undefined, true);
    const { data: contentBlockerTemplateMetaData } = useContentBlockerTemplateMetaData(currentStep >= 1, false, undefined, true);
    const releaseCreateQuery = useReleaseCreate(releaseRefId, releasableIds, disablePreReleaseClients);
    const { isFetching: releaseCreateQueryIsFetching } = releaseCreateQuery;
    // Modal step contents
    const steps = [
        {
            title: t("Choose templates"),
            content: /*#__PURE__*/ _jsx(ReleaseModalStepOne, {
                rowSelectionServices: {
                    selectedRowKeys: selectedServices,
                    onChange: (keys)=>{
                        setSelectedServices(keys.map(Number));
                    }
                },
                rowSelectionContentBlockers: {
                    selectedRowKeys: selectedContentBlockers,
                    onChange: (keys)=>{
                        setSelectedContentBlockers(keys.map(Number));
                    }
                }
            })
        },
        {
            title: t("Check release draft"),
            content: /*#__PURE__*/ _jsx(ReleaseModalStepTwo, {
                spinning: releaseCreateQueryIsFetching,
                selectedServiceIds: selectedServices,
                selectedContentBlockerIds: selectedContentBlockers,
                serviceValidations: serviceValidations,
                contentBlockerValidations: contentBlockerValidations,
                onValidationChange: (isValid)=>setIsValid(isValid),
                onReleasableIdsChange: (releasable)=>setReleasableIds(releasable)
            })
        }
    ];
    // Prepare validation results
    useEffect(()=>{
        const validations = (serviceTemplateMetaData || []).map((template)=>validateTemplateSuccessorOfIdentifier(template, serviceTemplateMetaData, t)).flat().filter(Boolean);
        setServiceValidations(validations);
        // lets compare if selected still available
        if (selectedServices?.length > 0) {
            const filtered = selectedServices.filter((id)=>serviceTemplateMetaData.findIndex((t)=>+t.id === +id) >= 0);
            if (filtered.length !== selectedServices.length) {
                setSelectedServices(filtered);
            }
        }
    }, [
        serviceTemplateMetaData
    ]);
    useEffect(()=>{
        const validations = (contentBlockerTemplateMetaData || []).map((template)=>validateTemplateSuccessorOfIdentifier(template, contentBlockerTemplateMetaData, t)).flat().filter(Boolean);
        setContentBlockerValidations(validations);
        // lets compare if selected still available
        if (selectedContentBlockers?.length > 0) {
            const filtered = selectedContentBlockers.filter((id)=>contentBlockerTemplateMetaData.findIndex((t)=>+t.id === +id) >= 0);
            if (filtered.length !== selectedContentBlockers.length) {
                setSelectedContentBlockers(filtered);
            }
        }
    }, [
        contentBlockerTemplateMetaData
    ]);
    // Modal functions
    const navigateStep = (direction = "fw")=>{
        if (direction === "fw") {
            if (currentStep < steps.length - 1) {
                setCurrentStep(currentStep + 1);
            }
        } else {
            if (currentStep > 0) {
                setCurrentStep(currentStep - 1);
            }
        }
    };
    // Release creation
    useEffect(()=>{
        if (releaseCreateQuery.isFetched) {
            if (releaseCreateQuery.isSuccess) {
                onReleased();
                notificationStore.add({
                    type: "success",
                    title: t("Release {{id}} successfully created!", {
                        id: releaseCreateQuery.data.id
                    }),
                    message: t("Updated {{serviceCount}} services and {{blockerCount}} content blockers", {
                        serviceCount: releaseCreateQuery.data.serviceTemplates?.length || 0,
                        blockerCount: releaseCreateQuery.data.contentBlockerTemplates?.length || 0
                    }),
                    closable: true,
                    duration: 10
                });
            } else if (releaseCreateQuery.isError) {
                notificationStore.addFromServerError(releaseCreateQuery.error, t);
            }
            setReleaseRefId(null); // reset state
        }
    }, [
        releaseCreateQuery
    ]);
    useEffect(()=>{
        if (open) {
            queryClient.refetchQueries(QueryKeys.contentBlockerMeta());
            queryClient.refetchQueries(QueryKeys.serviceMeta());
        } else {
            queryClient.refetchQueries(QueryKeys.getReleaseById(""));
            setSelectedServices([]);
            setSelectedContentBlockers([]);
            setDisablePreReleaseClients(undefined);
            setCurrentStep(0); // after release for example
        }
    }, [
        open
    ]);
    // A disabled button swallows hover events, so the tooltip variant needs a wrapper that can receive them
    const releaseButton = /*#__PURE__*/ _jsx(Button, {
        disabled: !canRelease,
        icon: /*#__PURE__*/ _jsx(TagOutlined, {}),
        type: "primary",
        loading: releaseCreateQuery.isFetching,
        style: canRelease ? undefined : {
            pointerEvents: "none"
        },
        children: t("Release now")
    });
    return /*#__PURE__*/ _jsxs(Modal, {
        open: open,
        title: title,
        destroyOnClose: true,
        onCancel: (e)=>{
            onCancel(e);
        },
        width: "90%",
        styles: {
            body: {
                // Set max height and enable vertical scroll
                maxHeight: DeviceType.isBigScreen(device) ? "75vh" : "65vh",
                overflowY: "auto"
            }
        },
        footer: /*#__PURE__*/ _jsxs("div", {
            style: {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 8
            },
            children: [
                currentStep === 1 && /*#__PURE__*/ _jsxs(Space, {
                    size: "small",
                    style: {
                        marginRight: "auto"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Tooltip, {
                            title: t("Select 'No' to keep the active states of the pre-release clients after the release."),
                            children: /*#__PURE__*/ _jsx(Typography.Text, {
                                type: "secondary",
                                children: t("Deactivate all pre-release clients after the release?")
                            })
                        }),
                        /*#__PURE__*/ _jsx(Radio.Group, {
                            size: "small",
                            value: disablePreReleaseClients,
                            onChange: (e)=>setDisablePreReleaseClients(e.target.value),
                            options: [
                                {
                                    label: t("Yes"),
                                    value: true
                                },
                                {
                                    label: t("No"),
                                    value: false
                                }
                            ],
                            optionType: "button",
                            buttonStyle: "solid"
                        })
                    ]
                }),
                currentStep > 0 && /*#__PURE__*/ _jsx(Button, {
                    icon: /*#__PURE__*/ _jsx(ArrowLeftOutlined, {}),
                    onClick: ()=>{
                        navigateStep("bw");
                    },
                    children: t("Back")
                }),
                currentStep === 0 && /*#__PURE__*/ _jsx(Button, {
                    icon: /*#__PURE__*/ _jsx(PartitionOutlined, {}),
                    type: "primary",
                    onClick: ()=>{
                        navigateStep("fw");
                    },
                    disabled: !(selectedContentBlockers?.length > 0 || selectedServices?.length > 0),
                    children: t("Prepare release draft")
                }),
                currentStep === 1 && (canRelease ? /*#__PURE__*/ _jsx(Popconfirm, {
                    title: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            t("Are you sure you want to create this release now?"),
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsxs(Space, {
                                children: [
                                    /*#__PURE__*/ _jsx(_Fragment, {
                                        children: t("Environment")
                                    }),
                                    /*#__PURE__*/ _jsx(EnvironmentTag, {
                                        showOnProduction: true
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx(_Fragment, {
                                children: disablePreReleaseClients ? t("All pre-release clients will be deactivated.") : t("All pre-release clients keep their current active state.")
                            })
                        ]
                    }),
                    placement: "top",
                    onConfirm: ()=>{
                        setReleaseRefId(`release-post-item-${v4()}`);
                    },
                    cancelText: t("No, wait"),
                    okText: t("Yes"),
                    children: releaseButton
                }) : /*#__PURE__*/ _jsx(Tooltip, {
                    title: releaseDisabledReason,
                    children: /*#__PURE__*/ _jsx("span", {
                        style: {
                            cursor: "not-allowed"
                        },
                        children: releaseButton
                    })
                }))
            ]
        }),
        children: [
            /*#__PURE__*/ _jsx(Steps, {
                current: currentStep,
                items: steps,
                style: {
                    marginBottom: "1rem"
                }
            }),
            /*#__PURE__*/ _jsx("div", {
                children: steps[currentStep].content
            })
        ]
    });
};
export { ReleaseModal };
